import { Avatar, Divider, Tooltip } from "@mui/material";
import React, { useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { s3baseUrl } from "../../config/config";
import { get_short_string } from "../../utils/constant";
import { useContentSetting } from "../../Hooks/ContentSetting";
import PanoramaIcon from "@mui/icons-material/Panorama";
import CallEndIcon from "@mui/icons-material/CallEnd";
import MicIcon from "@mui/icons-material/Mic";
import RecordNotFound from "../GeneralComponents/RecordNotFound";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { Icon } from "@iconify/react/dist/iconify.js";
import { read_all_chats_api } from "../../DAL/Messages/Messages";
import CustomPopover from "../GeneralComponents/CustomPopover";
import ConversationMenus from "./ConversationMenus";
import {
  BASED_ON,
  CHAT_FILTERS,
  date_to_display,
  filter_chats,
  isRTL,
  markdown_conversion,
  USER_TYPE,
} from "../../utils/classRoomUtils";
import CustomTabs from "../GeneralComponents/CustomTabs";
import { useTranslation } from "react-i18next";

const ConversationRow = React.memo(function ConversationRow({
  item,
  selectedConversationId,
  filterType,
  userInfo,
  favoriteIconSpacingClass,
  handleSelected,
  handleReadMessage,
  setConversationsList,
  t,
}) {
  const {
    is_favorite,
    members,
    _id,
    last_message,
    based_on,
    classTitle,
  } = item;
  let letter_to_show = 28;

  const isClassConversation = based_on === BASED_ON.CLASS;
  let conversations_user = isClassConversation
    ? null
    : members?.find((user) => user.user_type === USER_TYPE.STUDENT);

  const currentMember = members?.find((user) => user._id === userInfo._id);
  let unread_message_count = currentMember?.unread_message_count ?? 0;
  if (unread_message_count > 0) {
    letter_to_show -= 3;
  }
  if (is_favorite > 0) {
    letter_to_show -= 3;
  }

  const displayName = isClassConversation
    ? classTitle || t("class.fallbackTitle")
    : conversations_user?.name ?? "";
  const displayLetter = displayName ? displayName[0] : "?";

  return (
    <>
      <div
        className={`chatting-user p-2 ps-0 pe-0 ${
          _id === selectedConversationId ? "menuActive" : ""
        }`}
        onClick={() => {
          handleSelected(item);
        }}
      >
        <div className="d-flex align-items-center w-100">
          <div className="position-relative">
            <Avatar
              sx={{ width: 40, height: 40 }}
              src={
                isClassConversation
                  ? undefined
                  : s3baseUrl + (conversations_user?.profile_image ?? "")
              }
              className="conversation-avatar"
            >
              {displayLetter}
            </Avatar>

            {!isClassConversation && (
              <div className="styled-badge">
                <div
                  className={`user-badge ${
                    conversations_user?.is_online ? "user-online" : ""
                  }`}
                ></div>
              </div>
            )}
          </div>
          <div className="w-100 ps-2">
            <div className="user-name d-flex justify-content-between w-100">
              <h5>{displayName}</h5>
              {filterType !== "contacts" && last_message?.sender_id && (
                <div className="message-date mt-0 pt-0">
                  {last_message.message_type !== "call" &&
                    !isClassConversation &&
                    conversations_user?._id !== last_message?.sender_id && (
                      <span
                        className={`me-1 ${
                          last_message.message_status === "seen"
                            ? "message-status-seen"
                            : ""
                        }`}
                      >
                        {last_message.message_status === "sent" ? (
                          <DoneIcon />
                        ) : (
                          <DoneAllIcon />
                        )}
                      </span>
                    )}
                  <span>{date_to_display(last_message?.createdAt)}</span>
                </div>
              )}
            </div>
            {filterType !== "contacts" && (
              <div className="d-flex justify-content-between w-100">
                <div>
                  {last_message?.sender_id && (
                    <div className="last-message-chat">
                      {last_message?.message ? (
                        get_short_string(
                          markdown_conversion(last_message?.message),
                          letter_to_show
                        )
                      ) : last_message?.message_type === "call" ? (
                        <div className="last-message-box">
                          <CallEndIcon /> {t("chatting.conversations.call")}
                        </div>
                      ) : last_message?.images?.length > 0 ? (
                        <div className="last-message-box">
                          <PanoramaIcon /> {t("chatting.conversations.photo")}
                        </div>
                      ) : (
                        last_message?.recording_url && (
                          <div className="last-message-box">
                            <MicIcon />{" "}
                            {t("chatting.conversations.voiceMessage")}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
                <div className="chat-count-favorite">
                  {unread_message_count > 0 && (
                    <span className="unread_message_count">
                      {unread_message_count}
                    </span>
                  )}
                  {is_favorite && (
                    <div className={favoriteIconSpacingClass}>
                      <Icon icon="fa6-regular:star" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="ps-1">
            <ConversationMenus
              {...{ item, handleReadMessage, setConversationsList }}
            />
          </div>
        </div>
      </div>
      <Divider className="sidebar-divider" />
    </>
  );
});

export default function Conversations({
  conversationsList,
  handleSelected,
  selectedConversation,
  is_popup,
  setConversationsList,
  handleReadMessage,
}) {
  const { t, i18n } = useTranslation();
  const favoriteIconSpacingClass = isRTL(i18n.language) ? "me-2" : "ms-2";
  const [serachText, setSerachText] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [onlineType, setOnlineType] = useState("all");

  const { userInfo, unreadMessageCount } = useContentSetting();

  const handleChange = (event) => {
    setSerachText(event.target.value);
  };

  const handleOnline = () => {
    let type = "all";
    if (onlineType === "all") {
      type = "online";
    }
    setOnlineType(type);
  };

  const ReadAllChats = () => {
    setConversationsList?.((prevList) =>
      prevList.map((conversation) => ({
        ...conversation,
        members: conversation.members.map((member) =>
          member._id === userInfo._id
            ? { ...member, unread_message_count: 0 }
            : member
        ),
      }))
    );
    read_all_chats_api();
  };

  const MAIN_MENU_OPTIONS = [];
  if (unreadMessageCount > 0) {
    MAIN_MENU_OPTIONS.push({
      label: t("chatting.conversations.readAll"),
      icon: "solar:check-read-outline",
      handleClick: ReadAllChats,
    });
  }

  const chats_list = useMemo(
    () =>
      filter_chats(
        conversationsList,
        userInfo,
        serachText,
        filterType,
        onlineType
      ),
    [conversationsList, userInfo, serachText, filterType, onlineType]
  );

  const isOnline = Boolean(onlineType === "online");
  const languageCode = (i18n.resolvedLanguage || i18n.language || "en")
    .split(/[-_]/)[0]
    .toLowerCase();
  const isRtlUi = isRTL(languageCode);
  const selectedConversationId = selectedConversation?._id;

  return (
    <>
      <div
        className={`chatting-sidebar ${is_popup ? "pt-0" : ""} ${
          isRtlUi ? "rtl-conversations" : ""
        }`}
      >
        {!is_popup && (
          <div className="search-bar mb-2">
            <CustomTabs
              {...{
                tabs: CHAT_FILTERS,
                selected: filterType,
                handleChange: (item) => setFilterType(item.value),
                containerProps: { className: "mb-2 justify-content-between" },
              }}
            />
            <div className="filter-search-chats">
              <div className="search-icon">
                <SearchIcon />
              </div>
              <input
                type="text"
                value={serachText}
                onChange={handleChange}
                placeholder={t("chatting.conversations.search")}
                autoComplete="new-password"
                className="me-1"
              />
              <CustomPopover menu={MAIN_MENU_OPTIONS} data={{}} />
              <Tooltip title={isOnline ? t("chatting.conversations.seeAll") : t("chatting.conversations.seeOnline")}>
                <div
                  className={`input-online-box ${
                    isOnline ? "online-chats" : ""
                  }`}
                  onClick={handleOnline}
                  style={
                    isRtlUi
                      ? {
                          left: MAIN_MENU_OPTIONS.length > 0 ? "31px" : "16px",
                          right: "auto",
                        }
                      : {
                          right: MAIN_MENU_OPTIONS.length > 0 ? "31px" : "16px",
                          left: "auto",
                        }
                  }
                >
                  <Icon
                    icon="nrk:online"
                    className={`${isOnline ? "input-online-filter" : ""}`}
                  />
                </div>
              </Tooltip>
            </div>
          </div>
        )}
        <div className="chatting-users-list">
          {chats_list.length > 0 ? (
            chats_list.map((item) => (
              <ConversationRow
                key={item._id}
                item={item}
                selectedConversationId={selectedConversationId}
                filterType={filterType}
                userInfo={userInfo}
                favoriteIconSpacingClass={favoriteIconSpacingClass}
                handleSelected={handleSelected}
                handleReadMessage={handleReadMessage}
                setConversationsList={setConversationsList}
                t={t}
              />
            ))
          ) : (
            <RecordNotFound
              title={t("chatting.conversations.emptyTitle")}
              description={t("chatting.conversations.emptyDescription")}
              icon="mdi:forum-outline"
            />
          )}
        </div>
      </div>
    </>
  );
}
